import React from 'react'
import styles from './styles.module.scss'
import topology from 'lib/geodata/switzerland.json'
import ChoroplethMap from 'components/choroplethMap/'
import { SVGContainer } from 'components/svgContainer'
import ActiveMunicipalityPath from 'components/activeMunicipality'
import { geoIdentity, geoPath } from 'd3-geo'
import EventListenerMap from 'components/eventListenerMap'
import { feature, mesh } from 'topojson-client'

// region Type Definitions
export interface TaxMapProps {
  containerHeightRatio: number
  filter?: {
    property: string
    id: number
  }
  mouseOverHandler: (municpality: string, event: MouseEvent) => void
  patternScale: number
}

// endregion

/**
 * The main component. Draws a choropleth map of Swiss municipalities given a
 * data source.
 *
 * @param {object} props
 * @param props.mouseOverHandler
 */
export const TaxMap = (props: TaxMapProps) => {
  const {
    mouseOverHandler,
    filter,
    containerHeightRatio = 0.6,
    patternScale = 1
  } = props
  const mapId = `map-1`

  /**
   * Path generator function – needs to be defined in this part, since the
   * fitSize function depends on the applied filter for easy «zooming»
   */
  let pathGenerator = geoPath().projection(
    geoIdentity()
      .reflectY(true)
      .fitSize(
        [1000, 1000 * containerHeightRatio],
        feature(topology, topology.objects.kantone)
      )
  )

  const seenPfade = feature(topology, topology.objects.seen).features.map(
    (see, index) => {
      return (
        <path
          key={index}
          d={pathGenerator(see)}
          fill='#b8d5e6'
          className='lake'
          stroke='#a1b2bd'
          strokeWidth={1}
          vectorEffect='non-scaling-stroke'
        />
      )
    }
  )

  const kantonsMesh = (
    <path
      d={pathGenerator(
        mesh(topology, topology.objects.kantone, (a, b) => a !== b)
      )}
      stroke='#111'
      strokeLinejoin='round'
      fill='transparent'
      opacity={0.33}
      strokeWidth={2}
      vectorEffect='non-scaling-stroke'
    />
  )

  const municipalityMesh = (
    <path
      d={pathGenerator(mesh(topology, topology.objects.gemeinden))}
      stroke='#111'
      strokeLinejoin='round'
      fill='transparent'
      strokeOpacity={0.33}
      strokeWidth={1}
      vectorEffect='non-scaling-stroke'
    />
  )

  // Feature Detection for Internet Explorer and Edge, as they don't support
  // Drop Shadow Filters.
  const supportsDropShadowFilter =
    typeof window.SVGFEDropShadowElement !== 'undefined'

  return (
    <div
      className={styles.swissMap}
      id={mapId}
      onMouseLeave={event => mouseOverHandler('-1', event)}
    >
      <SVGContainer
        className={`choropleth ${styles.nonInteractive}`}
        width={1000}
        height={containerHeightRatio * 1000}
      >
        <defs>
          {supportsDropShadowFilter && (
            <filter id='shadow'>
              <feDropShadow dx='0 ' dy='0' stdDeviation='1' />
            </filter>
          )}
        </defs>
        <ChoroplethMap
          pathGenerator={pathGenerator}
          geometryCollection={feature(topology, topology.objects.gemeinden)}
          mapId={mapId}
        />
      </SVGContainer>

      <SVGContainer
        className='static'
        width={1000}
        height={containerHeightRatio * 1000}
      >
        <g className={`gemeinden ${styles.nonInteractive}`}>
          {municipalityMesh}
        </g>
        <g className={`kantone ${styles.nonInteractive}`}>{kantonsMesh}</g>
        <g className={`seen ${styles.nonInteractive}`}>{seenPfade}</g>
        <g className={'event-listener'}>
          <EventListenerMap
            pathGenerator={pathGenerator}
            geometryCollection={feature(topology, topology.objects.gemeinden)}
            mapId={mapId}
          />
        </g>
      </SVGContainer>

      {
        // Active Municipality has to be in its own container since it is
        // being redrawn all the time
      }
      <SVGContainer
        className={`active ${styles.nonInteractive}`}
        width={1000}
        height={containerHeightRatio * 1000}
      >
        <ActiveMunicipalityPath
          pathGenerator={pathGenerator}
          geometryCollection={feature(topology, topology.objects.gemeinden)}
          mapId={mapId}
        />
      </SVGContainer>
    </div>
  )
}

export default TaxMap
